import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  activateDeployment,
  createAgentRelease,
  createDeployment,
  createModelAlias,
  deleteAgentByName,
  deleteAgentRelease,
  deleteModelAlias,
  deprecateAgentRelease,
  getAgentById,
  getAgentDeployments,
  getAgentRelease,
  getAgentReleases,
  listModelAliases,
  publishAgentRelease,
  type UpdateReleaseDto,
  updateAgentBehaviorById,
  updateAgentById,
  updateAgentRelease,
  updateModelAlias,
} from '../agentApi';
import type {
  AgentDeployment,
  CreateDeploymentDto,
  CreateModelAliasDto,
  CreateReleaseDto,
  UpdateAgentBehaviorDto,
  UpdateAgentDto,
} from '../types';
import { agentKeys } from './use-agents';

export const agentDetailKeys = {
  detail: (id: string) => ['agents', 'detail', id] as const,
  releases: (id: string) => ['agents', 'releases', id] as const,
  deployments: (id: string) => ['agents', 'deployments', id] as const,
  modelAliases: (tenantSlug: string) => ['tenants', tenantSlug, 'model-aliases'] as const,
};

export function useAgentDetail(id: string | undefined) {
  return useQuery({
    queryKey: agentDetailKeys.detail(id ?? ''),
    enabled: !!id,
    queryFn: () => getAgentById(id as string),
  });
}

export function useAgentReleases(id: string | undefined) {
  return useQuery({
    queryKey: agentDetailKeys.releases(id ?? ''),
    enabled: !!id,
    queryFn: () => getAgentReleases(id as string),
  });
}

export function useAgentRelease(agentId: string | undefined, releaseId: string | undefined) {
  return useQuery({
    queryKey: ['agents', 'release', agentId ?? '', releaseId ?? ''],
    enabled: !!agentId && !!releaseId,
    queryFn: () => getAgentRelease(agentId as string, releaseId as string),
  });
}

export function useUpdateRelease(agentId: string, releaseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: UpdateReleaseDto) => updateAgentRelease(agentId, releaseId, dto),
    onSuccess: (updated) => {
      qc.setQueryData(['agents', 'release', agentId, releaseId], updated);
      void qc.invalidateQueries({ queryKey: agentDetailKeys.releases(agentId) });
    },
  });
}

export function useAgentDeployments(id: string | undefined) {
  return useQuery({
    queryKey: agentDetailKeys.deployments(id ?? ''),
    enabled: !!id,
    queryFn: async () => {
      const all = await getAgentDeployments(id as string);
      return all.filter((d) => {
        if (!d.agentId && !d.agentName) return true;
        return d.agentId === id || d.agentName === id;
      });
    },
  });
}

export function useUpdateAgent(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, dto }: { agentId: string; dto: UpdateAgentDto }) =>
      updateAgentById(agentId, dto),
    onSuccess: (updated) => {
      if (updated) {
        qc.setQueryData(agentDetailKeys.detail(id), updated);
      }
      void qc.invalidateQueries({ queryKey: agentKeys.all });
    },
  });
}

export function useUpdateAgentBehavior(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, dto }: { agentId: string; dto: UpdateAgentBehaviorDto }) =>
      updateAgentBehaviorById(agentId, dto),
    onSuccess: (updated) => {
      if (updated) {
        qc.setQueryData(agentDetailKeys.detail(id), updated);
      }
      void qc.invalidateQueries({ queryKey: agentKeys.all });
    },
  });
}

export function useCreateRelease(agentName: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateReleaseDto) => createAgentRelease(agentName, dto),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: agentDetailKeys.releases(agentName) });
    },
  });
}

export function usePublishRelease(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (releaseId: string) => publishAgentRelease(agentId, releaseId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: agentDetailKeys.releases(agentId) });
    },
  });
}

export function useDeprecateRelease(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (releaseId: string) => deprecateAgentRelease(agentId, releaseId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: agentDetailKeys.releases(agentId) });
    },
  });
}

export function useDeleteRelease(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (releaseId: string) => deleteAgentRelease(agentId, releaseId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: agentDetailKeys.releases(agentId) });
    },
  });
}

export function useActivateDeployment(agentName: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (deploymentId: string) => activateDeployment(deploymentId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: agentDetailKeys.deployments(agentName) });
    },
  });
}

export function useDeployRelease(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ dto }: { dto: CreateDeploymentDto }) => {
      const deployment = await createDeployment(dto);
      if (!deployment) return null;
      return activateDeployment(deployment.id);
    },
    onSuccess: (deployment: AgentDeployment | null) => {
      void qc.invalidateQueries({ queryKey: agentDetailKeys.deployments(agentId) });
      void qc.invalidateQueries({ queryKey: agentDetailKeys.releases(agentId) });
      if (deployment?.agentName) {
        void qc.invalidateQueries({ queryKey: agentDetailKeys.deployments(deployment.agentName) });
      }
    },
  });
}

export function useModelAliases(tenantSlug: string | undefined) {
  return useQuery({
    queryKey: agentDetailKeys.modelAliases(tenantSlug ?? ''),
    enabled: !!tenantSlug,
    queryFn: () => listModelAliases(tenantSlug as string),
  });
}

export function useCreateModelAlias(tenantSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateModelAliasDto) => createModelAlias(tenantSlug, dto),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: agentDetailKeys.modelAliases(tenantSlug) });
    },
  });
}

export function useUpdateModelAlias(tenantSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: Partial<CreateModelAliasDto> }) =>
      updateModelAlias(tenantSlug, id, dto),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: agentDetailKeys.modelAliases(tenantSlug) });
    },
  });
}

export function useDeleteAgent(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => deleteAgentByName(agentId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: agentKeys.all });
    },
  });
}

export function useDeleteModelAlias(tenantSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (aliasId: string) => deleteModelAlias(tenantSlug, aliasId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: agentDetailKeys.modelAliases(tenantSlug) });
    },
  });
}
